import React, { useState, useEffect } from "react";

interface IntegrationFormData {
  service_name: string;
  client_id: string;
  client_secret?: string;
  auth_url: string;
  token_url: string;
  revoke_url?: string;
  scopes: string[];
  additional_params?: Record<string, any>;
}

interface IntegrationFormProps {
  integration?: IntegrationFormData;
  onSubmit: (data: IntegrationFormData) => Promise<void>;
  onCancel: () => void;
  isEdit?: boolean;
}

export const IntegrationForm: React.FC<IntegrationFormProps> = ({
  integration,
  onSubmit,
  onCancel,
  isEdit = false,
}) => {
  const [formData, setFormData] = useState<IntegrationFormData>({
    service_name: "",
    client_id: "",
    client_secret: "",
    auth_url: "",
    token_url: "",
    revoke_url: "",
    scopes: [],
    additional_params: {},
  });
  const [scopesText, setScopesText] = useState("");
  const [paramsText, setParamsText] = useState("{}");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (integration) {
      setFormData(integration);
      setScopesText(Array.isArray(integration.scopes) 
        ? integration.scopes.join(", ") 
        : JSON.stringify(integration.scopes));
      setParamsText(JSON.stringify(integration.additional_params || {}, null, 2));
    }
  }, [integration]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.service_name.trim()) {
      newErrors.service_name = "Service name is required";
    }
    if (!formData.client_id.trim()) {
      newErrors.client_id = "Client ID is required";
    }
    if (!isEdit && !formData.client_secret?.trim()) {
      newErrors.client_secret = "Client Secret is required";
    }
    if (!formData.auth_url.trim()) {
      newErrors.auth_url = "Auth URL is required";
    } else {
      try {
        new URL(formData.auth_url);
      } catch {
        newErrors.auth_url = "Invalid URL format";
      }
    }
    if (!formData.token_url.trim()) {
      newErrors.token_url = "Token URL is required";
    } else {
      try {
        new URL(formData.token_url);
      } catch {
        newErrors.token_url = "Invalid URL format";
      }
    }
    if (formData.revoke_url && formData.revoke_url.trim()) {
      try {
        new URL(formData.revoke_url);
      } catch {
        newErrors.revoke_url = "Invalid URL format";
      }
    }

    // Validate JSON for additional params
    try {
      JSON.parse(paramsText);
    } catch {
      newErrors.additional_params = "Invalid JSON format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Parse scopes from comma-separated string
      const scopes = scopesText
        .split(",")
        .map(s => s.trim())
        .filter(s => s.length > 0);

      // Parse additional params
      const additional_params = JSON.parse(paramsText);

      const submitData: IntegrationFormData = {
        ...formData,
        scopes,
        additional_params,
      };

      console.log('Submitting integration data:', {
        ...submitData,
        client_secret: submitData.client_secret ? `[${submitData.client_secret.length} chars]` : 'empty'
      });

      await onSubmit(submitData);
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="service_name" className="block text-sm font-medium text-gray-700">
          Service Name
        </label>
        <input
          type="text"
          id="service_name"
          disabled={isEdit}
          value={formData.service_name}
          onChange={(e) => setFormData({ ...formData, service_name: e.target.value })}
          className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
            isEdit ? 'bg-gray-100' : ''
          }`}
          placeholder="e.g., github, slack, jira"
        />
        {errors.service_name && (
          <p className="mt-1 text-sm text-red-600">{errors.service_name}</p>
        )}
      </div>

      <div>
        <label htmlFor="client_id" className="block text-sm font-medium text-gray-700">
          Client ID
        </label>
        <input
          type="text"
          id="client_id"
          value={formData.client_id}
          onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        />
        {errors.client_id && (
          <p className="mt-1 text-sm text-red-600">{errors.client_id}</p>
        )}
      </div>

      <div>
        <label htmlFor="client_secret" className="block text-sm font-medium text-gray-700">
          Client Secret {isEdit && "(leave empty to keep existing)"}
        </label>
        <input
          type="password"
          id="client_secret"
          value={formData.client_secret}
          onChange={(e) => setFormData({ ...formData, client_secret: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          placeholder={isEdit ? "Enter new secret to update" : "Enter client secret"}
        />
        {errors.client_secret && (
          <p className="mt-1 text-sm text-red-600">{errors.client_secret}</p>
        )}
      </div>

      <div>
        <label htmlFor="auth_url" className="block text-sm font-medium text-gray-700">
          Authorization URL
        </label>
        <input
          type="url"
          id="auth_url"
          value={formData.auth_url}
          onChange={(e) => setFormData({ ...formData, auth_url: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          placeholder="https://example.com/oauth/authorize"
        />
        {errors.auth_url && (
          <p className="mt-1 text-sm text-red-600">{errors.auth_url}</p>
        )}
      </div>

      <div>
        <label htmlFor="token_url" className="block text-sm font-medium text-gray-700">
          Token URL
        </label>
        <input
          type="url"
          id="token_url"
          value={formData.token_url}
          onChange={(e) => setFormData({ ...formData, token_url: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          placeholder="https://example.com/oauth/token"
        />
        {errors.token_url && (
          <p className="mt-1 text-sm text-red-600">{errors.token_url}</p>
        )}
      </div>

      <div>
        <label htmlFor="revoke_url" className="block text-sm font-medium text-gray-700">
          Revoke URL (Optional)
        </label>
        <input
          type="url"
          id="revoke_url"
          value={formData.revoke_url}
          onChange={(e) => setFormData({ ...formData, revoke_url: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          placeholder="https://example.com/oauth/revoke"
        />
        {errors.revoke_url && (
          <p className="mt-1 text-sm text-red-600">{errors.revoke_url}</p>
        )}
      </div>

      <div>
        <label htmlFor="scopes" className="block text-sm font-medium text-gray-700">
          Scopes (comma-separated)
        </label>
        <input
          type="text"
          id="scopes"
          value={scopesText}
          onChange={(e) => setScopesText(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          placeholder="read, write, user:email"
        />
        <p className="mt-1 text-xs text-gray-500">
          Enter scopes separated by commas
        </p>
      </div>

      <div>
        <label htmlFor="additional_params" className="block text-sm font-medium text-gray-700">
          Additional Parameters (JSON)
        </label>
        <textarea
          id="additional_params"
          value={paramsText}
          onChange={(e) => setParamsText(e.target.value)}
          rows={4}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-mono text-xs"
          placeholder='{"prompt": "consent", "access_type": "offline"}'
        />
        {errors.additional_params && (
          <p className="mt-1 text-sm text-red-600">{errors.additional_params}</p>
        )}
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          {isSubmitting ? "Saving..." : isEdit ? "Update Integration" : "Create Integration"}
        </button>
      </div>
    </form>
  );
};